import React, { FC, useRef, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { Button } from '@packages/ui/shared';
import { useNavigate } from 'react-router-dom';
import SubaruLogo from '@web/vin-sessions/assets/placeholder_car_image.svg?react';
import { updateRectAttrs } from '@web/vin-sessions/utils/vinSessionUtils';

export interface VinWithExpiry {
    vin: string;
    expiresAt: Date;
    otpwAssignmentId: string;
}

interface ContinueToVinSessionModalProps {
    show: boolean;
    onHide: () => void;
    vins: VinWithExpiry[];
}

const useStyles = createUseStyles({
    modalBody: {
        padding: 24,
    },
    description: {
        fontFamily: 'Roboto',
        fontSize: 15,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '160%',
        color: '#071424',
        marginBottom: 24,
    },
    vinList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    vinCard: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        border: '1px solid #DEE6EF',
        borderRadius: 8,
        gap: 16,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: '#F5F8FC',
        },
    },
    imgContainer: {
        width: 60,
        minWidth: 60,
        height: 30,
        position: 'relative',
        flexShrink: 0,
    },
    vinInfo: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
    },
    vinNumber: {
        fontFamily: 'Roboto',
        fontSize: 15,
        fontWeight: 400,
        color: '#1F518F',
        textTransform: 'uppercase',
    },
    timer: {
        fontFamily: 'Roboto',
        fontSize: 13,
        fontWeight: 400,
        color: '#4C525D',
        whiteSpace: 'nowrap',
    },
    timerValue: {
        fontWeight: 600,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        padding: '16px 24px',
        borderTop: '1px solid #DEE6EF',
    },
});

const formatTimeRemaining = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return '00 : 00 : 00';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
};

const VinCard: FC<{ vinData: VinWithExpiry; onClick: () => void }> = ({ vinData, onClick }) => {
    const classes = useStyles();
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<number>(0);

    useEffect(() => {
        if (svgRef.current) {
            const rectElement = svgRef.current.querySelector('rect');
            if (rectElement) {
                updateRectAttrs(rectElement);
            }
        }
    }, [svgRef]);

    useEffect(() => {
        const calculateTimeRemaining = () => {
            const now = Date.now();
            const expiryTime = vinData.expiresAt.getTime();
            const diff = Math.max(0, Math.floor((expiryTime - now) / 1000));
            setTimeRemaining(diff);
        };

        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);

        return () => clearInterval(interval);
    }, [vinData.expiresAt]);

    return (
        <div
            className={classes.vinCard}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
        >
            <div className={classes.imgContainer}>
                <SubaruLogo ref={svgRef} />
            </div>
            <div className={classes.vinInfo}>
                <span className={classes.vinNumber}>{vinData.vin}</span>
                <span className={classes.timer}>
                    Expires in <span className={classes.timerValue}>{formatTimeRemaining(timeRemaining)}</span>
                </span>
            </div>
        </div>
    );
};

export const ContinueToVinSessionModal: FC<ContinueToVinSessionModalProps> = ({ show, onHide, vins }) => {
    const classes = useStyles();
    const navigate = useNavigate();

    useEffect(() => {
        if (!show) return;

        if (vins.length === 0) {
            onHide();
            return;
        }

        const latestExpiry = vins.reduce(
            (latest, v) => (v.expiresAt > latest ? v.expiresAt : latest),
            vins[0].expiresAt
        );

        const expiryTime = latestExpiry.getTime();

        const checkExpiry = () => {
            if (Date.now() >= expiryTime) {
                onHide();
            }
        };

        checkExpiry();
        const interval = setInterval(checkExpiry, 500);

        return () => clearInterval(interval);
    }, [show, vins, onHide]);

    const handleVinClick = (vin: string) => {
        onHide();
        navigate(`/vin/${vin}`);
    };

    const isSingleVin = vins.length === 1;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Continue to VIN Session</Modal.Title>
            </Modal.Header>
            <Modal.Body className={classes.modalBody}>
                <p className={classes.description}>
                    {isSingleVin
                        ? "You're about to open a VIN-related dashboard. You'll be able to access an authorization code required to complete the car key reprogramming."
                        : 'Select a VIN to open its dashboard and access the authorization code required to complete the car key reprogramming.'}
                </p>
                <div className={classes.vinList}>
                    {vins.map((vinData) => (
                        <VinCard key={vinData.vin} vinData={vinData} onClick={() => handleVinClick(vinData.vin)} />
                    ))}
                </div>
            </Modal.Body>
            <div className={classes.footer}>
                <Button variant="ghost-blue" onPress={onHide}>
                    Cancel
                </Button>
                {isSingleVin && (
                    <Button variant="primary" onPress={() => handleVinClick(vins[0].vin)}>
                        Continue
                    </Button>
                )}
            </div>
        </Modal>
    );
};
